import { useEffect, useState } from "react";
import { db } from "../../FIREBASE/firebase";
import { MoodItem, Room, ROOM_SUGGESTIONS, newId, fileToDataUrl } from "./home";
import { fetchPageMeta } from "../bikes/bikes";

// Uploaded photos live in their own docs (moodImages/{id}) — a home doc can't hold them.
// Items reference them as "fs:<id>"; resolved lazily and cached.
const imgCache = new Map<string, Promise<string>>();
const resolveImage = (value: string): Promise<string> => {
  if (!value.startsWith("fs:")) return Promise.resolve(value);
  const id = value.slice(3);
  if (!imgCache.has(id)) {
    imgCache.set(id, db.collection("moodImages").doc(id).get().then((d) => (d.data()?.data as string) || "").catch(() => { imgCache.delete(id); return ""; }));
  }
  return imgCache.get(id)!;
};

const MoodImg = ({ value, onClick }: { value: string; onClick: (src: string) => void }) => {
  const [src, setSrc] = useState<string>(value.startsWith("fs:") ? "" : value);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    setFailed(false);
    resolveImage(value).then((s) => { if (!alive) return; if (s) setSrc(s); else setFailed(true); });
    return () => { alive = false; };
  }, [value]);
  if (failed) return <div className="hem__mood-note">Kunde inte ladda bilden</div>;
  if (!src) return <div className="hem__mood-loading">…</div>;
  return <img src={src} alt="" loading="lazy" onClick={() => onClick(src)} onError={() => setFailed(true)} />;
};

const IMG_EXT = /\.(png|jpe?g|gif|webp|avif|svg)(\?|#|$)/i;

export const Moodboards = ({
  ownerUid, rooms, setRooms, planLabels, onClose,
}: {
  ownerUid: string;
  rooms: Room[];
  setRooms: (fn: (cur: Room[]) => Room[]) => void;
  planLabels: string[];
  onClose: () => void;
}) => {
  const [activeId, setActiveId] = useState<string | null>(rooms[0]?.id ?? null);
  const [newName, setNewName] = useState("");
  const [imgInput, setImgInput] = useState("");
  const [color, setColor] = useState("#c8b7a6");
  const [note, setNote] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [zoom, setZoom] = useState<string | null>(null);

  const active = rooms.find((r) => r.id === activeId) || rooms[0] || null;

  const addRoom = (name: string) => {
    const n = name.trim();
    if (!n) return;
    const existing = rooms.find((r) => r.name.toLowerCase() === n.toLowerCase());
    if (existing) { setActiveId(existing.id); setNewName(""); return; }
    const room: Room = { id: newId(), name: n, items: [] };
    setRooms((cur) => [...cur, room]);
    setActiveId(room.id);
    setNewName("");
  };
  const renameRoom = () => {
    if (!active) return;
    const n = window.prompt("Nytt namn på rummet", active.name);
    if (n && n.trim()) setRooms((cur) => cur.map((r) => (r.id === active.id ? { ...r, name: n.trim() } : r)));
  };
  const deleteRoom = () => {
    if (!active || !window.confirm(`Ta bort moodboarden för ${active.name}?`)) return;
    setRooms((cur) => cur.filter((r) => r.id !== active.id));
    setActiveId(null);
  };
  const addItems = (items: MoodItem[]) => {
    if (!active || items.length === 0) return;
    setRooms((cur) => cur.map((r) => (r.id === active.id ? { ...r, items: [...r.items, ...items] } : r)));
  };
  const removeItem = (id: string) => {
    if (!active) return;
    const gone = active.items.find((i) => i.id === id);
    if (gone?.kind === "image" && gone.value.startsWith("fs:")) {
      const fid = gone.value.slice(3);
      imgCache.delete(fid);
      db.collection("moodImages").doc(fid).delete().catch(() => {});
    }
    setRooms((cur) => cur.map((r) => (r.id === active.id ? { ...r, items: r.items.filter((i) => i.id !== id) } : r)));
  };

  const uploadFiles = async (files: FileList | null) => {
    if (!files || files.length === 0 || !active) return;
    setBusy(true); setMsg("");
    const items: MoodItem[] = [];
    for (const file of Array.from(files)) {
      if (!file.type.startsWith("image/")) continue;
      try {
        let data = await fileToDataUrl(file, 1100, 0.8);
        if (data.length > 850000) data = await fileToDataUrl(file, 800, 0.65);
        if (data.length > 900000) { setMsg("En bild var för stor — prova en mindre."); continue; }
        const ref = db.collection("moodImages").doc();
        await ref.set({ homeOwner: ownerUid, data, createdAt: Date.now() });
        imgCache.set(ref.id, Promise.resolve(data));
        items.push({ id: newId(), kind: "image", value: `fs:${ref.id}` });
      } catch { setMsg("Kunde inte ladda upp en av bilderna."); }
    }
    addItems(items);
    setBusy(false);
  };

  const addImages = async () => {
    const urls = imgInput.split(/\s+/).map((u) => u.trim()).filter((u) => /^https?:\/\//i.test(u));
    if (urls.length === 0) { setMsg("Klistra in en länk som börjar med https://"); return; }
    setBusy(true); setMsg("");
    const items: MoodItem[] = [];
    for (const u of urls) {
      if (IMG_EXT.test(u)) { items.push({ id: newId(), kind: "image", value: u }); continue; }
      const meta = await fetchPageMeta(u);
      if (meta?.image) items.push({ id: newId(), kind: "image", value: meta.image, link: u });
      else setMsg("Hittade ingen bild på en av länkarna — använd en direkt bildlänk (högerklicka på bilden → kopiera bildadress).");
    }
    addItems(items);
    setBusy(false);
    if (items.length) setImgInput("");
  };

  const unused = Array.from(new Set([...planLabels.filter((l) => l.trim().length <= 24 && !/\d/.test(l)), ...ROOM_SUGGESTIONS].map((s) => s.trim()).filter(Boolean)))
    .filter((s) => !rooms.some((r) => r.name.toLowerCase() === s.toLowerCase()));

  return (
    <div className="hem__overlay" onClick={onClose}>
      <div className="hem__modal hem__modal--wide" onClick={(e) => e.stopPropagation()}>
        <div className="hem__modal-head">
          <h3 className="hem__modal-title">🎨 Moodboards</h3>
          <button className="hem__modal-close" onClick={onClose} aria-label="Stäng">×</button>
        </div>
        <div className="hem__modal-body">
          <div className="hem__mood-tabs">
            {rooms.map((r) => (
              <button key={r.id} className={`hem__tool${active?.id === r.id ? " hem__tool--on" : ""}`} onClick={() => setActiveId(r.id)}>
                {r.name || "Rum"} <span className="hem__mood-count">{r.items.length}</span>
              </button>
            ))}
          </div>
          <div className="hem__form-row">
            <input className="hem__input" value={newName} placeholder="Nytt rum (t.ex. Sovrum)" onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addRoom(newName); }} />
            <button className="hem__btn" onClick={() => addRoom(newName)}>+ Rum</button>
          </div>
          {unused.length > 0 && (
            <div className="hem__mood-tabs">
              {unused.map((s) => <button key={s} className="hem__chip" onClick={() => addRoom(s)}>+ {s}</button>)}
            </div>
          )}

          {active ? (
            <>
              <div className="hem__mood-head">
                <b>{active.name}</b>
                <span className="hem__foot-spacer" />
                <button className="hem__link-btn" onClick={renameRoom}>Byt namn</button>
                <button className="hem__link-btn hem__link-btn--danger" onClick={deleteRoom}>Ta bort rum</button>
              </div>

              <div className="hem__form-row">
                <label className="hem__btn hem__btn--file">{busy ? "Laddar upp…" : "📤 Ladda upp bild"}
                  <input type="file" accept="image/*" multiple hidden disabled={busy} onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} />
                </label>
              </div>
              <div className="hem__form-row">
                <input className="hem__input" value={imgInput} placeholder="🖼️ Bildlänk (eller länk till en sida, t.ex. produkt)" onChange={(e) => setImgInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addImages(); }} />
                <button className="hem__btn" onClick={addImages} disabled={busy || !imgInput.trim()}>{busy ? "Hämtar…" : "Lägg till"}</button>
              </div>
              <div className="hem__form-row">
                <input className="hem__mood-color" type="color" value={color} onChange={(e) => setColor(e.target.value)} aria-label="Färg" />
                <button className="hem__btn" onClick={() => addItems([{ id: newId(), kind: "color", value: color }])}>+ Färg</button>
                <input className="hem__input" value={note} placeholder="📝 Anteckning (material, känsla…)" onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && note.trim()) { addItems([{ id: newId(), kind: "note", value: note.trim() }]); setNote(""); } }} />
                <button className="hem__btn" disabled={!note.trim()} onClick={() => { addItems([{ id: newId(), kind: "note", value: note.trim() }]); setNote(""); }}>+ Text</button>
              </div>
              {msg && <span className="hem__empty-hint">{msg}</span>}

              {active.items.length === 0 ? (
                <span className="hem__empty-hint">Tom moodboard — lägg till bilder, färger och anteckningar ovan.</span>
              ) : (
                <div className="hem__mood-board">
                  {active.items.map((i) => (
                    <div key={i.id} className={`hem__mood-item hem__mood-item--${i.kind}`}>
                      {i.kind === "image" && (
                        i.link
                          ? <a href={i.link} target="_blank" rel="noopener noreferrer"><img src={i.value} alt="" loading="lazy" /></a>
                          : <MoodImg value={i.value} onClick={setZoom} />
                      )}
                      {i.kind === "color" && <div className="hem__mood-swatch" style={{ background: i.value }}><span>{i.value.toUpperCase()}</span></div>}
                      {i.kind === "note" && <div className="hem__mood-note">{i.value}</div>}
                      <button className="hem__mood-del" onClick={() => removeItem(i.id)} aria-label="Ta bort">×</button>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <span className="hem__empty-hint">Skapa ett rum ovan för att börja på en moodboard.</span>
          )}
        </div>
        <div className="hem__modal-foot">
          <span className="hem__foot-spacer" />
          <button className="hem__btn" onClick={onClose}>Klar</button>
        </div>
      </div>
      {zoom && (
        <div className="hem__lightbox" onClick={(e) => { e.stopPropagation(); setZoom(null); }}>
          <img src={zoom} alt="" />
        </div>
      )}
    </div>
  );
};
